<template>
    <div>
        <v-dialog v-model="showDialog" max-width="600px">
            <v-card>
                <v-card-title class="headline align-start flex-column pa-2 primary primarytext--text">
                    <div class="font-weight-bold fill-width">
                        <span>{{ title }}</span>
                        <v-btn icon @click="closeDialog" class="float-right"><v-icon
                                class="primary primarytext--text">mdi-close</v-icon></v-btn>
                    </div>
                </v-card-title>
                <div v-if="oneMatch">
                    <div class="ma-4">
                        {{ $translate('The competition can be concluded once all results have been reported. Once the competition is concluded, no additional results can be reported.') }}
                    </div>
                    <div v-if="teamsWithoutResult && teamsWithoutResult.length > 0">
                        <div class="ma-4">
                            <div class="font-weight-bold">{{ missingResultsTitle }}</div>
                            <v-list>
                                <v-list-item v-for="team in teamsWithoutResult" :key="team.id"
                                    class="d-flex justify-space-between align-center">
                                    <v-list-item-content>
                                        <v-list-item-title>{{ team.name }}</v-list-item-title>
                                    </v-list-item-content>
                                    <v-list-item-action>
                                        <v-btn small rounded fab color="primary" @click="addResult(team)">
                                            <v-icon>mdi-plus</v-icon>
                                        </v-btn>
                                    </v-list-item-action>
                                </v-list-item>
                            </v-list>
                        </div>
                    </div>
                    <v-card-actions class="ma-2">
                        <v-btn width="50%" class="zoezi-button-thin primarytext--text" color="secondary" @click="closeDialog">{{ $translate("Cancel") }}</v-btn>
                        <v-btn width="50%" class="zoezi-button-thin primarytext--text" :disabled="teamsWithoutResult.length > 0" color="primary" @click="setResult" :loading="saving">{{ $translate("Finish") }}</v-btn>
                    </v-card-actions>
                </div>
                <div v-else>
                    <v-form v-model="formValid">
                        <div class="ma-4">
                            <div v-for="(field, index) in teamFields" :key="index">
                                <v-card class="ma-2">
                                    <v-card-title>
                                        <div class="font-weight-bold fill-width">
                                            <span>{{ field.title }}</span>
                                        </div>
                                    </v-card-title>
                                    <v-card-text>
                                        <form-field v-if="field.resultTypeField" v-model="field.resultTypeField.value" :field="field.resultTypeField"
                                            star dense></form-field>
                                        <form-field
                                            v-if="match.result_type == 'padel' || field.resultTypeField.value == 0"
                                            v-model="field.resultField.value"
                                            :field="field.resultField" star
                                            dense
                                            >
                                        </form-field>
                                        <v-alert v-if="match.result_type == 'padel'" dense outlined type="info">{{ $translate('Example') }}: 6-2, 4-6, 1-6</v-alert>
                                    </v-card-text>
                                </v-card>
                            </div>
                            <form-field v-model="finishMatchField.value" :field="finishMatchField"></form-field>
                        </div>
                    </v-form>
                    <v-card-actions>
                        <v-btn width="100%" class="zoezi-button-thin mb-2 primarytext--text" :disabled="!formValid" color="primary" @click="setResult" :loading="saving">{{ $translate("Save") }}</v-btn>
                    </v-card-actions>
                </div>
            </v-card>
        </v-dialog>
        <teamsport-event-dialog :match="match" :teamId="selectedTeam.id" v-model="showEventDialog" oneMatch v-if="showEventDialog" />
    </div>
</template>

<script>
import TeamsportEventDialog from './TeamsportEventDialog.vue';
import FormField from '../components/FormField.vue';
import { resultRules } from '../util';

export default {
    name: 'zoezi-teamsport-set-result-dialog',

    components: {
        TeamsportEventDialog,
        FormField,
    },

    props: {
        value: {
            type: Boolean,
            default: false,
        },
        match: {
            type: Object,
            required: true,
        },
        oneMatch: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            loading: false,
            loadingInvalidResults: false,
            invalidResults: [],
            missingFields: [],
            teamFields: [],
            formValid: false,
            saving: false,
            events: [],
            eventTypes: [],
            selectedTeam: null,
            unsubscribe: [],
            showEventDialog: false,
            finishMatchField: {
                name: 'finishMatch',
                title: 'Match is finished',
                type: 'checkbox',
                value: true,
            }
        };
    },

    computed: {
        showDialog: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },
        teamsWithoutResult() {
            return this.match.teams.filter(team => {
                const result = this.match.result?.find(result => result?.team_id == team.id);
                return !result?.result && !result?.invalid_id;
            });
        },
        missingResultsTitle() {
            return this.teamsWithoutResult.length === 1
                ? this.$translate('Result is missing for the following participant:')
                : this.$translate('Results are missing for the following participants:');
        },
        title() {
            return this.oneMatch ? this.$translate('Mark as finished') : this.$translate('Set result');
        }
    },

    methods: {
        possiblyShowError(response) {
            if (!response.ok) {
                response.json().then(data => {
                    this.$store.commit('showErrorDialog', { title: this.$translate('Error'), text: `${this.$translate('Something went wrong')}. ${this.$translate(data?.message)}`});
                });
            }
        },
        initializeTeamFields() {
            if (!this.match || !this.match.teams) return;

            if (this.match.result_type == 'padel') {
                const result = this.match.result ? this.match.result[0] : null;
                this.teamFields = [{
                    title: this.match.teams[0].name + ' - ' + this.match.teams[1].name,
                    resultField: {
                        title: 'Result',
                        type: 'text',
                        required: true,
                        value: result?.result,
                        rules: resultRules(this.match?.result_type),
                    }
                }];
            } else {
                this.teamFields = this.match.teams.map(team => {
                    const result = this.match.result?.find(r => r?.team_id === team.id);
                    return {
                        title: team.name,
                        id: team.id,
                        resultTypeField: {
                            name: 'resultType_' + team.id,
                            title: 'Result type',
                            type: 'select',
                            required: true,
                            data: this.invalidResults.map(ir => ({
                                value: ir.id,
                                text: ir.name,
                            })),
                            value: result?.invalid_id || 0,
                        },
                        resultField: {
                            name: 'result_' + team.id,
                            title: 'Result',
                            type: 'text',
                            required: true,
                            value: result?.result,
                            rules: resultRules(this.match?.result_type),

                        }
                    };
                });
            }
        },
        getInvaldResults() {
            this.loadingInvalidResults = true;
            this.$api.get('/api/teamsport/resultinvalid/get').then(response => {
                response.unshift({ id: 0, name: this.$translate('Valid result') });
                this.invalidResults = response;
                this.initializeTeamFields();
                this.loadingInvalidResults = false;
            });
        },
        closeDialog() {
            this.selectedTeam = null;
            this.showEventDialog = false;
            this.showDialog = false;
        },
        setResult() {
            this.saving = true;
            let data = null;
            if (this.match.result_type == 'padel') {
                data = {
                    match_id: this.match.id,
                    result: this.teamFields[0].resultField.value,
                    finished: this.finishMatchField.value,
                };
            } else if (this.oneMatch) {
                data = {
                    match_id: this.match.id,
                    result: this.match.result,
                    finished: true,
                };
            } else {
                data = {
                    match_id: this.match.id,
                    result: this.teamFields.map(field => {
                        return {
                            team_id: field.id,
                            invalid_id: field.resultTypeField.value == 0 ? null : field.resultTypeField.value,
                            result: field.resultField.value || null,
                        }
                    }),
                    finished: this.finishMatchField.value,
                };
            }
            this.$api.post('/api/teamsport/match/setresult', data).then((response) => {
                this.saving = false;
                this.closeDialog();
                this.possiblyShowError(response)
            });
        },
        addResult(item) {
            this.selectedTeam = item;
            this.showEventDialog = true;
        }
    },

    mounted() {
        this.getInvaldResults();
        this.unsubscribe.push(this.$api.subscribe({
            type: 'teamsportresultinvalid',
            callback: () => this.getInvaldResults()
        }));
    },

    destroyed() {
        this.unsubscribe.forEach(unsubscribe => unsubscribe());
    },
}

</script>